import React, { useCallback, useEffect, useMemo, useState } from "react";
import BaseStockChart from "./BaseStockChart";
import ShiftModalShell from "./ShiftModalShell";
import { ChartDataService } from "../../services/ChartDataService";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import { useThemeClasses } from "../../providers/ThemeProvider";
import { useThemedCharts } from "../../hooks/useThemedCharts";

export type ShiftDetailMetric = "efficiency" | "utilization";

export interface ShiftDetailModalProps {
  machineSerialNo: string;
  /** Shift calendar day, "YYYY-MM-DD". */
  shiftDate: string;
  /** Shift name as stored in Doris, e.g. "A1" — without the "Shift " prefix. */
  shiftName: string;
  metric: ShiftDetailMetric;
  /**
   * The clicked bar's own value, passed straight through rather than
   * recomputed. The per-shift bars come from `mv_shift_metrics`; the curves
   * below come from `production_period`, and the two pipelines don't define
   * efficiency identically — so the headline must come from the bar to stay
   * consistent with what the user clicked.
   */
  shiftValue?: number | null;
  /** Target line value in percent, when the machine defines one. */
  target?: number | null;
  onClose: () => void;
}

/** One per-minute sample inside the shift (PC.machine_kpi_over_shift). */
interface ShiftSample {
  t: number;
  /** Units produced during this minute — a true per-minute rate. */
  output: number | null;
  /** Cumulative-from-shift-start efficiency at the end of this minute. */
  efficiency: number | null;
  /** Cumulative-from-shift-start utilization at the end of this minute. */
  utilization: number | null;
}

/** Normalise a NATS response body into a flat array of rows. */
function asRows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === "object") {
    const results = (data as { results?: unknown }).results;
    if (Array.isArray(results)) return results as Record<string, unknown>[];
  }
  return [];
}

function numOrNull(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Epoch ms for a bucket timestamp, anchored to LOCAL time.
 *
 * Doris sends wall-clock shift times with no zone ("2023-10-27 20:07:00"; the
 * NATS service may hand them over as ISO with a trailing Z). The chart renders
 * on the browser's local time zone, so building the date from its parts is what
 * keeps 20:07 in the data showing as 20:07 on the axis.
 */
function localTimestamp(raw: string): number {
  const m = raw.match(
    /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!m) return NaN;
  return new Date(
    +m[1],
    +m[2] - 1,
    +m[3],
    +m[4],
    +m[5],
    m[6] ? +m[6] : 0,
  ).getTime();
}

/**
 * Drill-down popup for one shift, opened by left-clicking a bar in the
 * per-shift charts (which show a single aggregate and hide how the shift ran).
 *
 * Two curves, because `production_period` offers two genuinely different things
 * and conflating them would misrepresent the data:
 *
 *  - **Output per minute** — the sum of each period's `good_production` delta.
 *    A true instantaneous rate, and it re-sums to the shift's own output figure
 *    exactly, so it's the trustworthy view of when the machine actually ran.
 *  - **Cumulative KPI** — `efficiency`/`utilization_ratio` are stored as
 *    running-to-date ratios, not per-minute rates. They dip when a stoppage
 *    inflates the denominator and recover as production resumes. The axis and
 *    series say "cumulative" because reading them as instantaneous would be
 *    wrong.
 */
const ShiftDetailModal: React.FC<ShiftDetailModalProps> = ({
  machineSerialNo,
  shiftDate,
  shiftName,
  metric,
  shiftValue,
  target,
  onClose,
}) => {
  const { token } = useUnifiedAuth();
  const themeClasses = useThemeClasses();
  const { getStatusColors, getSeriesColor, isDark } = useThemedCharts();
  const [samples, setSamples] = useState<ShiftSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isEfficiency = metric === "efficiency";
  const label = isEfficiency ? "Efficiency" : "Utilization";

  const load = useCallback(async () => {
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ChartDataService.fetchMachineKpiOverShift(
        {
          serialno: machineSerialNo,
          shift_date: shiftDate,
          shift_name: shiftName,
        },
        token,
      );

      if (!response.success) {
        throw new Error(response.error ?? "Request failed");
      }

      setSamples(
        asRows(response.data)
          .map((r) => ({
            t: localTimestamp(String(r.bucket ?? "")),
            output: numOrNull(r.output),
            efficiency: numOrNull(r.efficiency),
            utilization: numOrNull(r.utilization),
          }))
          .filter((s) => Number.isFinite(s.t))
          .sort((a, b) => a.t - b.t),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load shift detail");
    } finally {
      setLoading(false);
    }
  }, [machineSerialNo, shiftDate, shiftName, token]);

  useEffect(() => {
    load();
  }, [load]);

  const outputColor = getSeriesColor(0);
  const kpiColor = getStatusColors.info;

  const series = useMemo(
    () => [
      {
        name: "Output (units/min)",
        type: "area" as const,
        color: outputColor,
        yAxis: 0,
        data: samples.map((s) => [s.t, s.output] as [number, number | null]),
      },
      {
        name: `${label} — cumulative (%)`,
        type: "line" as const,
        color: kpiColor,
        yAxis: 1,
        data: samples.map(
          (s) =>
            [s.t, isEfficiency ? s.efficiency : s.utilization] as [
              number,
              number | null,
            ],
        ),
      },
    ],
    [samples, isEfficiency, label, outputColor, kpiColor],
  );

  const totalOutput = useMemo(
    () => samples.reduce((sum, s) => sum + (s.output ?? 0), 0),
    [samples],
  );

  const axisLabelStyle = {
    color: getStatusColors.yAxisLabels,
    fontSize: "12px",
  };
  const axisTitleStyle = {
    color: getStatusColors.yAxisTitle,
    fontSize: "13px",
    fontWeight: "600",
  };

  const subtitle = (
    <>
      {shiftDate} • Shift {shiftName} • {machineSerialNo}
      {typeof shiftValue === "number" && (
        <>
          {" • "}
          <span className={themeClasses.textSecondary}>
            shift {metric}: <b>{shiftValue.toFixed(1)}%</b>
          </span>
        </>
      )}
      {totalOutput > 0 && (
        <>
          {" • "}
          <span className={themeClasses.textSecondary}>
            output: <b>{totalOutput.toLocaleString()}</b>
          </span>
        </>
      )}
    </>
  );

  return (
    <ShiftModalShell
      title={`${label} over shift`}
      subtitle={subtitle}
      onClose={onClose}
    >
      {/*
          Extra bottom padding: BaseStockChart sizes its root to the chart
          `height` but renders its export/show-data controls (and the optional
          data table) *below* that, so they'd spill past the card border.
        */}
      <div className="px-4 pt-2 pb-14 max-h-[85vh] overflow-y-auto">
        <BaseStockChart
          type="line"
          height={420}
          series={series}
          loading={loading}
          error={error}
          onRetry={load}
          legend={{ enabled: true, position: "bottom" }}
          navigator={{ enabled: true, height: 60 }}
          exporting={{
            enabled: true,
            filename: `${machineSerialNo}-${shiftDate}-shift-${shiftName}-${metric}`,
          }}
          noDataMessage={`No production periods recorded for this shift`}
          customOptions={{
            // Two units on one chart, so the axes are declared here as an
            // array. An array in customOptions replaces the base's single
            // themed axis outright (see deepMerge), hence the explicit
            // styling below.
            yAxis: [
              {
                title: { text: "Output (units/min)", style: axisTitleStyle },
                labels: { style: axisLabelStyle },
                min: 0,
                gridLineColor: isDark
                  ? "rgba(71, 85, 105, 0.3)"
                  : "rgba(226, 232, 240, 0.8)",
                opposite: false,
              },
              {
                title: {
                  text: `${label} — cumulative (%)`,
                  style: axisTitleStyle,
                },
                labels: { format: "{value}%", style: axisLabelStyle },
                // Soft bounds only: the cumulative ratios legitimately leave
                // 0–100 in the raw data, and a hard max would clip those
                // points out of the plot and out of reach of the tooltip.
                softMin: 0,
                softMax: 100,
                gridLineWidth: 0,
                opposite: true,
                plotLines:
                  target !== null && target !== undefined
                    ? [
                        {
                          value: target,
                          color: getStatusColors.plotLine,
                          dashStyle: "Dash" as const,
                          width: 2,
                          label: {
                            text: `${label} Target`,
                            align: "right" as const,
                            style: { color: getStatusColors.plotLine },
                          },
                        },
                      ]
                    : [],
              },
            ],
            tooltip: {
              shared: true,
              outside: true,
              formatter: function (this: any) {
                const time = new Date(this.x).toLocaleTimeString();
                let html = `<b>${time}</b><br/>`;
                const points =
                  this.points && this.points.length
                    ? this.points
                    : this.point
                      ? [this.point]
                      : [];
                for (const p of points) {
                  const isPct = p.series.name.includes("%");
                  const v =
                    typeof p.y === "number"
                      ? isPct
                        ? `${p.y.toFixed(1)}%`
                        : p.y.toLocaleString()
                      : "-";
                  html += `<span style="color:${p.series.color}">●</span> ${p.series.name}: <b>${v}</b><br/>`;
                }
                return html;
              },
            },
          }}
        />
      </div>
    </ShiftModalShell>
  );
};

export default ShiftDetailModal;
